import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { toast } from 'react-toastify';
import LoadingSpinner from '../Common/LoadingSpinner';
import DashboardLayout from '../Layout/DashboardLayout';
import './ConnectWhatsApp.css';

const ConnectWhatsApp = () => {
    const [loading, setLoading] = useState(false);
    const { refreshProfile } = useAuth();
    const navigate = useNavigate();

    // Environment Variables - APP VERSION v22.0 is the current Meta stable
    const FB_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '1298260612181970';
    const FB_CONFIG_ID = process.env.REACT_APP_FACEBOOK_CONFIG_ID || '3302994339877898';
    const COEXISTENCE_CONFIG_ID = process.env.REACT_APP_FACEBOOK_COEXISTENCE_CONFIG_ID || '3253773585012301';
    const SOLUTION_ID = process.env.REACT_APP_SOLUTION_PARTNER_ID || '722513957493714';
    const SDK_VERSION = process.env.REACT_APP_FB_SDK_VERSION || 'v22.0';
    const REDIRECT_URI = process.env.REACT_APP_WHATSAPP_CALLBACK_URL || '';

    useEffect(() => {
        // Define global init function for Meta SDK
        window.fbAsyncInit = function () {
            try {
                console.log(`📡 [SDK] Initializing Meta SDK version ${SDK_VERSION}...`);
                window.FB.init({
                    appId: FB_APP_ID,
                    autoLogAppEvents: true,
                    xfbml: true,
                    version: SDK_VERSION
                });
                console.log('✅ [SDK] Meta SDK initialized successfully.');
                window.__FB_INITIALIZED__ = true;
            } catch (err) {
                console.error('❌ [SDK] Meta SDK initialization failed:', err);
            }
        };

        // Load or Re-init SDK
        if (window.FB && window.__FB_INITIALIZED__) {
            console.log('📡 [SDK] Meta SDK already initialized.');
        } else if (window.FB) {
            console.log('📡 [SDK] Meta SDK script loaded but not initialized. Running init...');
            window.fbAsyncInit();
        } else {
            console.log('📡 [SDK] Injecting Meta SDK script...');
            const js = document.createElement('script');
            js.id = 'facebook-jssdk';
            js.src = "https://connect.facebook.net/en_US/sdk.js";
            js.async = true;
            js.defer = true;
            document.head.appendChild(js);
        }

        const handleEmbeddedSignupEvent = (event) => {
            const isFacebook = typeof event.origin === 'string' && (
                event.origin === "https://www.facebook.com" ||
                event.origin === "https://web.facebook.com" ||
                event.origin.endsWith(".facebook.com") ||
                event.origin === window.location.origin
            );

            if (!isFacebook) return;

            try {
                let data = event.data;
                if (typeof data === "string") {
                    try {
                        data = JSON.parse(data);
                    } catch (e) {
                        // ignore non-json
                    }
                }

                if (data && (data.type === "WA_EMBEDDED_SIGNUP" || data.event === "FINISH" || data.current_step)) {
                    console.log("📩 Embedded Signup Event:", data);

                    const metaData = data.data || data;
                    const wabaId = metaData.waba_id || metaData.wabaId || metaData.business_account_id || metaData.businessAccountId;
                    const phoneId = metaData.phone_number_id || metaData.phoneNumberId || metaData.phone_id || metaData.phoneId;

                    if (wabaId || phoneId) {
                        console.log("🎯 Received Embedded Signup IDs:", { wabaId, phoneId });

                        window.__WA_EMBEDDED_DATA__ = {
                            wabaId: wabaId || window.__WA_EMBEDDED_DATA__?.wabaId,
                            phoneId: phoneId || window.__WA_EMBEDDED_DATA__?.phoneId
                        };
                    }
                }
            } catch (err) {
                // Ignore non-JSON strings from Facebook SDK
            }
        };

        window.addEventListener("message", handleEmbeddedSignupEvent);

        return () => {
            window.removeEventListener("message", handleEmbeddedSignupEvent);
        };
    }, [FB_APP_ID, SDK_VERSION]);

    const launchWhatsAppSignup = (isCoexistence = false) => {
        if (!window.FB) {
            toast.error('Meta SDK is not loaded. Please wait or refresh.');
            return;
        }

        if (!window.__FB_INITIALIZED__) {
            console.warn('⚠️ [SDK] SDK not yet initialized. Attempting late init...');
            window.fbAsyncInit();
        }

        try {
            const configIdToUse = isCoexistence ? COEXISTENCE_CONFIG_ID : FB_CONFIG_ID;
            console.log(`🚀 [SDK] Launching FB.login (Coexistence: ${isCoexistence}, ConfigID: ${configIdToUse})...`);
            const scope = 'whatsapp_business_management,whatsapp_business_messaging';

            const extras = {
                feature: 'whatsapp_embedded_signup',
                session_info_version: 2,
                sessionInfoVersion: 2,
                setup: {
                    solution_id: SOLUTION_ID,
                    solutionID: SOLUTION_ID
                }
            };

            // Add featureType for WhatsApp Business App Coexistence onboarding
            if (isCoexistence) {
                extras.featureType = 'whatsapp_business_app_onboarding';
            }

            window.FB.login((response) => {
                console.log('📬 [SDK] Login Response:', response);

                if (response.authResponse) {
                    const code = response.authResponse.code;
                    const wabaId = window.__WA_EMBEDDED_DATA__?.wabaId;
                    const phoneId = window.__WA_EMBEDDED_DATA__?.phoneId;

                    console.log('🎯 [SDK] Captured:', { wabaId, phoneId });
                    toast.info('Meta authorization success!', { autoClose: 2000 });

                    setLoading(true);
                    handleConnectionWithCode(code, wabaId, phoneId);
                } else {
                    console.warn('⚠️ [SDK] No authResponse - setup cancelled.');
                    toast.warning('Setup cancelled.');
                }
            }, {
                config_id: configIdToUse,
                response_type: 'code',
                override_default_response_type: true,
                scope: scope,
                extras: extras
            });
        } catch (err) {
            console.error('❌ [SDK] FB.login failed:', err);
            toast.error('SDK Initialization Error.');
        }
    };

    const handleConnectionWithCode = async (code, businessAccountId, phoneNumberId) => {
        try {
            console.log('🚀 [BACKEND] Connecting account...', { businessAccountId, phoneNumberId });
            const response = await api.put('/user/connect-whatsapp', {
                code,
                businessAccountId,
                phoneNumberId,
                redirectUri: REDIRECT_URI
            });

            if (response.success) {
                await refreshProfile();
                toast.success('Number connected successfully!');
                navigate('/templates');
            } else {
                toast.error(response.message || 'Failed to connect.');
            }
        } catch (error) {
            console.error('❌ [BACKEND] API Error:', error);
            const errMsg = error.response?.data?.message || 'Could not complete connection.';
            toast.error(errMsg);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="dashboard-content">
                    <div className="connect-whatsapp-container">
                        <LoadingSpinner size="large" message="Authenticating with Meta..." />
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="dashboard-content">
                <div className="page-header">
                    <div className="header-content">
                        <h1>WhatsApp Integration</h1>
                        <p>Link your Business Account to activate MessagePe campaigns.</p>
                    </div>
                </div>

                <div className="connect-whatsapp-container">
                    <div className="connect-card">
                        <div className="icon-badge">
                            <i className="fab fa-whatsapp"></i>
                        </div>

                        <div className="connect-header">
                            <h1>Connect with Meta</h1>
                            <p>Choose how you would like to connect your WhatsApp account below.</p>
                        </div>

                        <div className="onboarding-options-grid">
                            {/* Option 1: WhatsApp Business App Coexistence */}
                            <div className="onboarding-option-card coexistence-option">
                                <div className="option-badge">Coexistence</div>
                                <div className="option-icon-wrapper whatsapp-color">
                                    <i className="fab fa-whatsapp"></i>
                                </div>
                                <div className="option-details">
                                    <h3>Link Business App Number</h3>
                                    <p>Connect your existing WhatsApp Business mobile app number to Cloud API without losing your phone chat access.</p>
                                </div>
                                <button 
                                    onClick={() => launchWhatsAppSignup(true)} 
                                    className="btn-whatsapp-coexistence"
                                >
                                    <i className="fab fa-whatsapp"></i>
                                    Link Business Number
                                </button>
                            </div>

                            {/* Option 2: New Number / Standard API */}
                            <div className="onboarding-option-card standard-option">
                                <div className="option-badge standard-badge">Standard API</div>
                                <div className="option-icon-wrapper meta-color">
                                    <i className="fab fa-facebook-square"></i>
                                </div>
                                <div className="option-details">
                                    <h3>Onboard New Number</h3>
                                    <p>Register a new, fresh phone number directly to WhatsApp Cloud API for high-scale messaging and campaigns.</p>
                                </div>
                                <button 
                                    onClick={() => launchWhatsAppSignup(false)} 
                                    className="btn-whatsapp-standard"
                                >
                                    <i className="fab fa-facebook-square"></i>
                                    Connect New Number
                                </button>
                            </div>
                        </div>

                        <div className="benefits-grid">
                            <div className="benefit-item">
                                <i className="fas fa-shield-alt benefit-icon"></i>
                                <div className="benefit-text">
                                    <h3>Meta Certified</h3>
                                    <p>Official secure authentication flow.</p>
                                </div>
                            </div>
                            <div className="benefit-item">
                                <i className="fas fa-bolt benefit-icon"></i>
                                <div className="benefit-text">
                                    <h3>Quick Link</h3>
                                    <p>Account setup done in minutes.</p>
                                </div>
                            </div>
                            <div className="benefit-item">
                                <i className="fas fa-sync benefit-icon"></i>
                                <div className="benefit-text">
                                    <h3>Cloud Sync</h3>
                                    <p>Numbers synced automatically.</p>
                                </div>
                            </div>
                        </div>

                        <div className="connect-actions">
                            <p className="privacy-note">Using Official Meta SDK ({SDK_VERSION})</p>
                        </div>
                    </div>

                    <div className="step-guide">
                        <h2>Three Simple Steps</h2>
                        <div className="steps-row">
                            <div className="step">
                                <div className="step-number">1</div>
                                <p>Authenticate</p>
                            </div>
                            <div className="step-line"></div>
                            <div className="step">
                                <div className="step-number">2</div>
                                <p>Authorize</p>
                            </div>
                            <div className="step-line"></div>
                            <div className="step">
                                <div className="step-number">3</div>
                                <p>Success</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default ConnectWhatsApp;
